// TEMPORARY MOCK ADAPTER for Counseling Manual (상담매뉴얼, PRD 11).
// In-memory only — state lives in this module's `store` + `tree` and resets on
// hard reload. Mutations resolve with ~300 ms simulated latency. Errors mimic
// the backend envelope (ManualApiError). Backend switch: VITE_USE_MOCK_API=false
// flips to the real HTTP adapter (counseling-manual.adapter.ts).
//
// TODO(pro-item-design): the category tree is seeded here from the prototype
// MockData.topics because the PRO Item Design page is not implemented in React
// yet, AND because per the build plan this feature owns its OWN editable copy
// of the tree (Category Manager). Both facts mean the seed lives here for now.

import {
  MANUAL_GRADES,
  ManualApiError,
  type ConsultationManual,
  type ConsultationManualApi,
  type ConsultationManualDetail,
  type ContentBlock,
  type CreateManualInput,
  type ManualCategoryTree,
  type ManualGrade,
  type ManualListPage,
  type ManualListParams,
  type ManualStatus,
  type UpdateManualInput,
  type UploadResult,
} from '../types/counseling-manual.types';
import type { ManualSortKey, SortDir } from '../store/counseling-manual-page.store';
import { filterManuals, sortManuals } from '../utils/filter-sort';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Seed: category tree (prototype MockData.topics, flattened to area→sub→items) ─
interface SeedTopic {
  area: string;
  subArea: string;
  items: string[];
}

const SEED_TOPICS: SeedTopic[] = [
  { area: '정서·인지 증상 관리', subArea: '정서 증상', items: ['지속적인 우울감', '자살사고', '불안의 급격한 악화'] },
  { area: '정서·인지 증상 관리', subArea: '약물 관련', items: ['복약 상태', '약물 부작용 체감도'] },
  { area: '정서·인지 증상 관리', subArea: '수면·피로', items: ['불면·과수면'] },
  { area: '신체·생활습관 요인 관리', subArea: '건강행태', items: ['신체활동 악화', '고위험 음주 여부'] },
  { area: '신체·생활습관 요인 관리', subArea: '일상생활 구조 유지', items: ['규칙적인 식사 유지', '규칙적인 수면 유지'] },
  { area: '신체·생활습관 요인 관리', subArea: '관계 회복', items: ['사회적 접촉 회피'] },
  { area: '신체·생활습관 요인 관리', subArea: '역할 수행', items: ['일상생활수행능력 유지'] },
  { area: '사회·환경적 지원', subArea: '의료자원', items: ['상담 창구 확보'] },
  { area: '사회·환경적 지원', subArea: '복지자원', items: ['사회보장 서비스 접근성'] },
  { area: '사회·환경적 지원', subArea: '지역사회 사례관리', items: ['지역사회 사례관리 연계 유지'] },
  { area: '사회·환경적 지원', subArea: '디지털 자기기록', items: ['디지털 자기기록 지속성'] },
  { area: '자기관리 및 가족·자원 연계', subArea: '질병 인식', items: ['환자 본인의 질병 및 증상 이해도', '치료 접근 장벽 인식'] },
  { area: '자기관리 및 가족·자원 연계', subArea: '보호자 이해도·돌봄 역량', items: ['보호자 질병 이해도', '응급 상황 대처 방법 인지'] },
  { area: '자기관리 및 가족·자원 연계', subArea: '돌봄 부담 및 안전', items: ['돌봄 부담 체감도', '위기 시 비상 연락'] },
];

function buildSeedTree(): ManualCategoryTree {
  const areas: ManualCategoryTree['areas'] = [];
  const byArea = new Map<string, ManualCategoryTree['areas'][number]>();
  for (const topic of SEED_TOPICS) {
    let area = byArea.get(topic.area);
    if (!area) {
      area = { name: topic.area, subAreas: [] };
      byArea.set(topic.area, area);
      areas.push(area);
    }
    let sub = area.subAreas.find((s) => s.name === topic.subArea);
    if (!sub) {
      sub = { name: topic.subArea, items: [] };
      area.subAreas.push(sub);
    }
    for (const item of topic.items) {
      if (!sub.items.includes(item)) sub.items.push(item);
    }
  }
  return { areas };
}

let tree: ManualCategoryTree = buildSeedTree();

// ── Seed: manuals (prototype MockData.consultationManuals, ids 45–50) ────────
interface DetailRecord {
  id: number;
  title: string;
  area: string;
  subArea: string;
  item: string;
  grade: ManualGrade | null;
  blocks: ContentBlock[];
  contentHtml: string;
  preview: string;
  status: ManualStatus;
  publishedAt: string | null;
}

const seed: DetailRecord[] = [
  { id: 50, title: '조현병 환자의 질병 인식 향상 전략', area: '자기관리 및 가족·자원 연계', subArea: '질병 인식', item: '', grade: 'G3', blocks: [], contentHtml: '', preview: '환자와 가족이 질병을 이해하고 치료에 적극 참여하도록 돕는 단계별 상담 전략과 예시 대화를 포함합니다.', status: 'PUBLISHED', publishedAt: '2026.03.15' },
  { id: 49, title: '항정신병 약물 복용 순응도 개선 방법', area: '정서·인지 증상 관리', subArea: '약물 관련', item: '', grade: 'G3', blocks: [], contentHtml: '', preview: '복약 순응도 저하의 주요 원인을 분석하고 동기 강화 기법과 부작용 상담 방법을 소개합니다.', status: 'PUBLISHED', publishedAt: '2026.03.12' },
  { id: 48, title: '수면 위생 교육 및 피로 관리 지침', area: '정서·인지 증상 관리', subArea: '수면·피로', item: '', grade: 'G1', blocks: [], contentHtml: '', preview: '수면 환경 개선·일상 리듬 관리·피로 자가 점검 체크리스트를 통한 예방적 수면 위생 가이드입니다.', status: 'PUBLISHED', publishedAt: '2026.03.10' },
  { id: 47, title: '사회적 낙인 극복을 위한 상담 접근법', area: '신체·생활습관 요인 관리', subArea: '관계 회복', item: '', grade: 'G2', blocks: [], contentHtml: '', preview: '낙인 내재화 수준 평가와 자기효능감·탄력성 강화 상담 기법, 환자·가족용 워크북을 제공합니다.', status: 'PUBLISHED', publishedAt: '2026.03.08' },
  { id: 46, title: '인지기능 훈련 프로그램 안내', area: '정서·인지 증상 관리', subArea: '정서 증상', item: '', grade: 'G2', blocks: [], contentHtml: '', preview: '기억·주의·실행기능 향상을 위한 8주 훈련 모듈 구성과 회기별 운영 가이드를 담았습니다.', status: 'DRAFT', publishedAt: null },
  { id: 45, title: '우울·불안 증상 관리 상담 매뉴얼', area: '정서·인지 증상 관리', subArea: '정서 증상', item: '', grade: 'G2', blocks: [], contentHtml: '', preview: '증상 강도별 상담 시나리오와 자살·자해 위험 신호 스크리닝 체크리스트를 제공합니다.', status: 'PUBLISHED', publishedAt: '2026.03.05' },
];

let store: DetailRecord[] = clone(seed);

// ── Helpers ──────────────────────────────────────────────────────────────────
function cidOf(id: number): string {
  // PRD §4.1 — 'C-###' zero-padded to 3 digits.
  return `C-${String(id).padStart(3, '0')}`;
}

function idOfCid(cid: string): number {
  return Number.parseInt(cid.replace(/\D/g, ''), 10);
}

function nextId(): number {
  return store.reduce((max, r) => (r.id > max ? r.id : max), 0) + 1;
}

// Today as YYYY.MM.DD in KST. PRD §4.3.
function todayKst(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()).replace(/-/g, '.');
}

function validateTitle(title: string | undefined, forPublish: boolean): string {
  const cleaned = (title ?? '').trim();
  if (forPublish && !cleaned) {
    throw new ManualApiError('TITLE_REQUIRED', '제목을 입력해주세요.', {
      fields: { title: '제목을 입력해주세요.' },
    });
  }
  if (cleaned.length > 200) {
    throw new ManualApiError('TITLE_TOO_LONG', '제목은 200자 이내로 입력해주세요.', {
      fields: { title: '제목은 200자 이내로 입력해주세요.' },
    });
  }
  return cleaned;
}

function validateGrade(grade: ManualGrade | null | undefined): ManualGrade {
  if (!grade || !MANUAL_GRADES.includes(grade)) {
    throw new ManualApiError('GRADE_REQUIRED', 'Grade를 선택해주세요.', {
      fields: { grade: 'Grade를 선택해주세요.' },
    });
  }
  return grade;
}

function validateCascade(area?: string, subArea?: string, item?: string): {
  area: string;
  subArea: string;
  item: string;
} {
  const a = (area ?? '').trim();
  const s = (subArea ?? '').trim();
  const i = (item ?? '').trim();
  if (!a || !s || !i) {
    throw new ManualApiError(
      !a ? 'AREA_REQUIRED' : !s ? 'SUB_AREA_REQUIRED' : 'ITEM_REQUIRED',
      '영역, 하위영역, 항목을 모두 선택해주세요.',
      { fields: { cascade: '영역, 하위영역, 항목을 모두 선택해주세요.' } },
    );
  }
  return { area: a, subArea: s, item: i };
}

function toListRow(r: DetailRecord): ConsultationManual {
  return {
    cid: cidOf(r.id),
    title: r.title,
    grade: r.grade,
    area: r.area,
    subArea: r.subArea,
    item: r.item,
    preview: r.preview,
    status: r.status,
    publishedAt: r.publishedAt,
  };
}

function toDetail(r: DetailRecord): ConsultationManualDetail {
  // SHCT-456 — the real BE returns the entity's audit updated_at. The in-memory
  // mock has no audit column, so stand in with publishedAt (published rows) or
  // today (drafts) — enough to prove the preview reads a DB value, not new Date().
  return {
    ...toListRow(r),
    blocks: clone(r.blocks),
    contentHtml: r.contentHtml,
    updatedAt: r.publishedAt ?? todayKst(),
  };
}

function findOrThrow(cid: string): DetailRecord {
  const found = store.find((r) => r.id === idOfCid(cid));
  if (!found) throw new ManualApiError('NOT_FOUND', '매뉴얼을 찾을 수 없습니다.');
  return found;
}

export const counselingManualMockApi: ConsultationManualApi = {
  async list(params: ManualListParams): Promise<ManualListPage> {
    await delay();
    // Mirror the server-side contract (C2): filter → sort → paginate, return the
    // page + total. Uses the same filter/sort helpers the FE unit-tests cover.
    const all = store.map(toListRow);
    const filtered = filterManuals(all, {
      area: params.area ?? '',
      subArea: params.subArea ?? '',
      search: params.keyword ?? '',
    });
    const sorted = sortManuals(
      filtered,
      (params.sort ?? 'cid') as ManualSortKey,
      (params.direction ?? 'desc') as SortDir,
    );
    const size = params.size > 0 ? params.size : 20;
    const page = params.page > 0 ? params.page : 1;
    const start = (page - 1) * size;
    return clone({ items: sorted.slice(start, start + size), page, size, total: sorted.length });
  },

  async get(cid: string): Promise<ConsultationManualDetail> {
    await delay();
    return toDetail(findOrThrow(cid));
  },

  async create(input: CreateManualInput): Promise<ConsultationManualDetail> {
    await delay();
    const forPublish = input.status === 'PUBLISHED';
    const title = validateTitle(input.title, forPublish);
    let area = input.area ?? '';
    let subArea = input.subArea ?? '';
    let item = input.item ?? '';
    let grade = input.grade;
    if (forPublish) {
      ({ area, subArea, item } = validateCascade(input.area, input.subArea, input.item));
      grade = validateGrade(input.grade);
    }

    const record: DetailRecord = {
      id: nextId(),
      title,
      area,
      subArea,
      item,
      // DRAFT keeps whatever grade the user picked (may be null); PUBLISH is
      // grade-validated above. No G1 default — PRD 11: grade has no default.
      grade: grade ?? null,
      blocks: clone(input.blocks ?? []),
      contentHtml: input.contentHtml ?? '',
      preview: input.preview ?? '',
      status: input.status,
      publishedAt: forPublish ? todayKst() : null,
    };
    store = [record, ...store];
    return toDetail(record);
  },

  async update(cid: string, input: UpdateManualInput): Promise<ConsultationManualDetail> {
    await delay();
    const current = findOrThrow(cid);
    const nextStatus = input.status ?? current.status;
    const forPublish = nextStatus === 'PUBLISHED';

    const title = input.title !== undefined ? validateTitle(input.title, forPublish) : current.title;
    let { area, subArea, item } = current;
    if (forPublish) {
      ({ area, subArea, item } = validateCascade(
        input.area ?? current.area,
        input.subArea ?? current.subArea,
        input.item ?? current.item,
      ));
    } else {
      area = input.area ?? current.area;
      subArea = input.subArea ?? current.subArea;
      item = input.item ?? current.item;
    }
    const grade = forPublish
      ? validateGrade(input.grade ?? current.grade)
      : input.grade ?? current.grade;

    const updated: DetailRecord = {
      ...current,
      title,
      area,
      subArea,
      item,
      grade,
      blocks: input.blocks !== undefined ? clone(input.blocks) : current.blocks,
      contentHtml: input.contentHtml ?? current.contentHtml,
      preview: input.preview ?? current.preview,
      status: nextStatus,
      // PUBLISHED save (initial or re-save) refreshes the date; DRAFT clears it.
      publishedAt: forPublish ? todayKst() : null,
    };
    store = store.map((r) => (r.id === current.id ? updated : r));
    return toDetail(updated);
  },

  async remove(cid: string): Promise<void> {
    await delay();
    const target = findOrThrow(cid);
    store = store.filter((r) => r.id !== target.id);
  },

  // Read-only tree, sourced from PRO Item Design (SSoT). Classification
  // Management mutations were removed — ADM.019/020, PRD 11 v1.2.
  async getCategoryTree(): Promise<ManualCategoryTree> {
    await delay();
    return clone(tree);
  },

  // SHCT-454 — mock upload: validate JPG/PNG ≤5MB (BE does the authoritative
  // magic-byte sniff), then return a base64 data: URL in place of a storage URL
  // so the image still renders in offline/mock dev.
  async uploadImage(file: File): Promise<UploadResult> {
    await delay();
    const okType = /^image\/(jpeg|png)$/.test(file.type);
    const okSize = file.size <= 5 * 1024 * 1024;
    if (!okType || !okSize) {
      throw new ManualApiError('UNKNOWN', '이미지 업로드에 실패했습니다. 파일 형식과 크기를 확인해 주세요.');
    }
    const url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    return { url };
  },
};
